import { useMemo, useState } from 'react';
import { Actions, Async, FSpread } from '@client/new/helpers/types';
import {
  BundlePricing,
  BundleStatus,
  useCreateOtpBundleMutation,
  useDeleteOtpBundleMutation,
  useGetOtpBundlesQuery,
  useGetSellableContentItemsQuery,
  useUpdateOtpBundleMutation,
} from 'generated/graphql';
import {
  AllFeatures,
  RawPrograms,
  AllPrograms,
  AllContent,
  RawContentItem,
  RawOtpBundle,
} from '../../common/bundle.types';
import { Control, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { toFeatures } from '../../subscription/model/bundle.model.mapper';
import {
  allowedContentTypes,
  toAllContent,
  toSellableContent,
} from '../../common/bundles.mapper';
import {
  getBundleFormDataByBundleId,
  getMutationErrors,
  mapToggledContent,
  MutationErrors,
  removeContent,
  toAllOtpPrograms,
  toCreateBundlePayload,
  ToggleOtpBundlePayload,
  toSavedContentIds,
  toSavedProgramIds,
  toSellableOtpPrograms,
  toUpdateBundlePayload,
  validateContentForPublish,
} from './otpBundles.model.mapper';
import uuid4 from 'uuid4';
import auth from '@client/components/auth';
import { isNil } from 'lodash';

export type FormMode = 'edit' | 'create' | 'duplicate' | null;

export type NewBundleSubmit = Array<{ id: string; title: string }>;
export type EditBundleSubmit = Array<{ id: number; title: string }>;

export type BundlesModelData = {
  isAdmin: boolean;
  allFeatures: AllFeatures;
  allContent: AllContent;
  sellableContent: AllContent;
  allPrograms: AllPrograms;
  sellablePrograms: AllPrograms;
  otpBundles: RawOtpBundle[];
  control: Control<BundleFormValues>;
  submittedNewBundle: NewBundleSubmit;
  submittedEditBundle: EditBundleSubmit;
  form: {
    published: boolean;
    readonly: boolean;
    mode: FormMode;
    wasPurchased: boolean;
    bundleId: number | null;
    data: {
      features: number[];
      programs: number[];
      content: string[];
    };
    error: string | string[] | null;
    savedProgramIds: number[];
    savedContentIds: string[];
  };
};

type PayloadMap = {
  toggleBundleContent: ToggleOtpBundlePayload;
  removeBundleContent: ToggleOtpBundlePayload;
  saveBundle: null;
  saveBundleAndRedirect: { callback: () => void };
  publishBundle: null;
  archiveBundle: null;
  deleteBundle: null;
  bundleForm: {
    mode: FormMode;
    bundleId: number | null;
  };
};

export type BundlesModelAction = Actions<PayloadMap>;
export type BundlesModelDispatcher = FSpread<PayloadMap>;

export type OtpBundlesModel = BundlesModelData & {
  dispatch: BundlesModelDispatcher;
};

export const otpBundleSchema = z.object({
  name: z.string(),
  price: z.string(),
  description: z.string().optional(),
  programs: z.array(z.number()).nullable().optional(),
  content: z.array(z.string()).nullable().optional(),
  features: z.array(z.number()).nullable().optional(),
  published: z.boolean().default(false),
  archived: z.boolean().default(false),
  wasPurchased: z.boolean().default(false),
  playbookFee: z.number().optional(),
});

export type BundleFormValues = z.infer<typeof otpBundleSchema>;

export type State = {
  formMode: FormMode;
  bundleId: null | number;
  formError: string | string[] | null;
  submittedNewBundle: NewBundleSubmit;
  submittedEditBundle: EditBundleSubmit;
};

export const formDefaultalues = {
  name: undefined,
  price: undefined,
  description: undefined,
  programs: [],
  features: [],
  content: [],
  published: false,
  archived: false,
  playbookFee: undefined,
};

export const useOtpBundlesModel = ({
  creatorId,
}: {
  creatorId: number;
}): Async<OtpBundlesModel> => {
  const isAdmin = auth.isAdministrator();

  const [state, setState] = useState<State>({
    formMode: null,
    bundleId: null,
    formError: null,
    submittedNewBundle: [],
    submittedEditBundle: [],
  });

  const [createOtpBundle] = useCreateOtpBundleMutation();
  const [updateOtpBundle] = useUpdateOtpBundleMutation();
  const [deleteOtpBundle] = useDeleteOtpBundleMutation();

  const { data, loading, error, refetch } = useGetOtpBundlesQuery({
    variables: {
      creatorId: creatorId,
      bundlePricing: BundlePricing.Otp,
    },
    fetchPolicy: 'network-only',
  });

  const { data: contentData } = useGetSellableContentItemsQuery({
    variables: {
      creatorId: String(creatorId),
    },
    fetchPolicy: 'network-only',
  });

  const rawContent = (contentData?.content?.contentItems || []).filter((item) =>
    allowedContentTypes.includes(item.type),
  ) as RawContentItem[];

  const allPrograms = toAllOtpPrograms(data?.collections as RawPrograms);
  const allContent = rawContent ? toAllContent(rawContent) : [];
  const initialOtpBundles = (data?.bundle || []).filter(
    (b) => b.pricing === BundlePricing.Otp,
  ) as RawOtpBundle[];
  const edittedBundle = useMemo(
    () =>
      isNil(state?.bundleId)
        ? null
        : initialOtpBundles.find((b) => b.id === state.bundleId),
    [state.bundleId, initialOtpBundles],
  );

  const { handleSubmit, watch, setValue, control, reset } =
    useForm<BundleFormValues>({
      defaultValues: formDefaultalues,
      resolver: zodResolver(otpBundleSchema),
    });

  if (loading) {
    return {
      type: 'fetching',
    };
  }

  if (error) {
    return {
      type: 'fetch-error',
      value: 'Could not fetch bundle deatils',
    };
  }

  const features = watch('features') || [];
  const programs = watch('programs') || [];
  const content = watch('content') || [];
  const published = watch('published');
  const archived = watch('archived');
  const wasPurchased = watch('wasPurchased');

  const dispatch: BundlesModelDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as BundlesModelAction;
    switch (action.type) {
      case 'toggleBundleContent':
        mapToggledContent(
          programs,
          features,
          content,
          action.payload,
          setValue,
          state,
          setState,
        );
        return null;
      case 'removeBundleContent':
        removeContent(programs, features, content, action.payload, setValue);
        return null;
      case 'saveBundle':
        saveBundle();
        return null;
      case 'saveBundleAndRedirect':
        saveBundle().then(() => action.payload.callback());
        return null;
      case 'publishBundle':
        publishBundle();
        return null;
      case 'archiveBundle':
        archiveBundle();
        return null;
      case 'deleteBundle':
        deleteBundle();
        return null;
      case 'bundleForm':
        setBundleForm(action.payload);
        return null;
    }
  };

  const saveBundle = handleSubmit((values) => {
    state.formMode === 'edit' ? updateBundle(values) : createBundle(values);
  });

  const createBundle = async (
    values: BundleFormValues,
    status?: BundleStatus,
  ) => {
    const input = toCreateBundlePayload(creatorId, values, status);
    const id = uuid4();

    setState((prev) => ({
      ...prev,
      submittedNewBundle: [
        ...prev.submittedNewBundle,
        { id, title: input.title },
      ],
    }));

    try {
      const res = await createOtpBundle({
        variables: {
          input: input,
        },
        errorPolicy: 'all',
      });
      if (res.data?.createOtpBundle?.warnings) {
        const error = res.data?.createOtpBundle?.warnings.message;
        setState((prev) => ({ ...prev, formError: error }));
        return;
      }
      if (res?.errors) {
        const errors = getMutationErrors(
          res.errors as MutationErrors,
          allPrograms,
          allContent,
        );
        setState((prev) => ({ ...prev, formError: errors }));
        return;
      }

      toast.success(`Offer ${values.name} was created successfully`);
      setState((prev) => ({
        ...prev,
        formMode: null,
        bundleId: null,
      }));
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setState((prev) => ({
        ...prev,
        submittedNewBundle: prev.submittedNewBundle.filter((i) => i.id !== id),
      }));
      refetch();
    }
  };

  const updateBundle = async (
    values: BundleFormValues,
    status?: BundleStatus,
  ) => {
    if (!edittedBundle) {
      toast.error('Cannot save offer updates');
      return;
    }

    const input = toUpdateBundlePayload(
      edittedBundle.id,
      values,
      status || edittedBundle.status,
      isAdmin,
    );
    setState((prev) => ({
      ...prev,
      submittedEditBundle: [
        ...prev.submittedEditBundle,
        { id: edittedBundle.id, title: input.title },
      ],
    }));
    try {
      const res = await updateOtpBundle({
        variables: {
          input: input,
        },
        errorPolicy: 'all',
      });
      if (res.data?.updateOtpBundle?.warnings) {
        const error = res.data?.updateOtpBundle?.warnings.message;
        setState((prev) => ({ ...prev, formError: error }));
        return;
      }
      if (res?.errors) {
        const errors = getMutationErrors(
          res.errors as MutationErrors,
          allPrograms,
          allContent,
        );
        setState((prev) => ({ ...prev, formError: errors }));
        return;
      }
      toast.success(`Offer ${edittedBundle.title} was updated successfully`);
      setState((prev) => ({
        ...prev,
        formMode: null,
        bundleId: null,
      }));
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setState((prev) => ({
        ...prev,
        submittedEditBundle: prev.submittedEditBundle.filter(
          (i) => i.id !== edittedBundle.id,
        ),
      }));
      refetch();
    }
  };

  const publishBundle = handleSubmit((values) => {
    const publishError = validateContentForPublish(programs, features, content);
    if (publishError) {
      setState((prev) => ({ ...prev, formError: publishError }));
      return;
    }

    state.formMode === 'edit'
      ? updateBundle(values, BundleStatus.Published)
      : createBundle(values, BundleStatus.Published);
  });

  const archiveBundle = handleSubmit((values) => {
    updateBundle(values, BundleStatus.Archived);
  });

  const deleteBundle = () => {
    if (!state.bundleId) {
      toast.error('Cannot delete offer');
      return;
    }
    deleteOtpBundle({
      variables: {
        bundleId: state.bundleId,
      },
    })
      .then(() => {
        toast.success(`Offer id:${state.bundleId} was deleted successfully`);
        setState((prev) => ({
          ...prev,
          formMode: null,
          bundleId: null,
        }));
      })
      .catch((e) => {
        toast.error(e.message);
      })
      .finally(() => {
        refetch();
      });
  };

  const setBundleForm = ({
    mode,
    bundleId,
  }: {
    mode: FormMode;
    bundleId: number | null;
  }) => {
    const values =
      mode === 'create' || !bundleId
        ? formDefaultalues
        : getBundleFormDataByBundleId(
            bundleId,
            initialOtpBundles,
            rawContent,
            allPrograms,
            mode,
          );
    setState((prev) => ({
      ...prev,
      formMode: mode,
      bundleId,
      formError: null,
    }));
    reset(values, {
      keepValues: false,
      keepDirty: false,
      keepDirtyValues: false,
    });
  };

  return {
    type: 'fetched',
    value: {
      dispatch,
      control,
      isAdmin,
      otpBundles: initialOtpBundles,
      allPrograms,
      sellablePrograms: toSellableOtpPrograms(data?.collections as RawPrograms),
      allFeatures: data?.feature ? toFeatures(data?.feature) : [],
      allContent,
      sellableContent: rawContent ? toSellableContent(rawContent) : [],
      submittedEditBundle: state.submittedEditBundle,
      submittedNewBundle: state.submittedNewBundle,
      form: {
        savedProgramIds: toSavedProgramIds(edittedBundle?.items),
        savedContentIds: toSavedContentIds(edittedBundle?.items),
        published: published,
        wasPurchased: wasPurchased,
        readonly: archived,
        mode: state.formMode,
        bundleId: state.bundleId,
        error: state.formError,
        data: {
          features,
          programs,
          content,
        },
      },
    },
  };
};
